import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '../../../config/token';
import { Request } from '../../../config/requests';
import { showToast } from '../../../config/helpers';
import Loading from '../../../components/common/Loading';
import { isMobileOnly } from 'react-device-detect';

const formatYMD = (v) => {
    if (!v) return '';
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) {
        const ms = n > 1e12 ? n : n * 1000;
        const d = new Date(ms);
        if (!isNaN(d)) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        }
    }
    return '';
};
const formatAmount = (v) => (v == null || Number.isNaN(Number(v)) ? '' : Number(v).toFixed(2));

const RemoveDunningsSelective = ({ onBack, onCloseModal }) => {
    const { t } = useTranslation();
    const token = getToken();

    const [policyNo, setPolicyNo] = useState('');
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [isRemoving, setIsRemoving] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [clearPolicy, setClearPolicy] = useState(false); // always visible

    const hasSelection = selected.size > 0;
    const allSelected = useMemo(
        () => records.length > 0 && selected.size === records.length,
        [records.length, selected.size]
    );

    // Auto-toggle behavior:
    // - when transitioning to "all selected" -> set clearPolicy = true
    // - when transitioning away from "all selected" (user unselects) -> set clearPolicy = false
    const prevAllSelected = useRef(allSelected);
    useEffect(() => {
        if (!prevAllSelected.current && allSelected) {
            // rising edge: not-all -> all
            setClearPolicy(true);
        } else if (prevAllSelected.current && !allSelected) {
            // falling edge: all -> not-all (unselect)
            setClearPolicy(false);
        }
        prevAllSelected.current = allSelected;
    }, [allSelected]);

    const toggle = (id, checked) => {
        const next = new Set(selected);
        if (checked) next.add(id);
        else next.delete(id);
        setSelected(next);
    };

    const toggleAll = (checked) => {
        if (checked) setSelected(new Set(records.map((r) => r.id)));
        else setSelected(new Set());
    };

    const loadRecords = async () => {
        if (!policyNo || !email) return;
        setIsLoading(true);
        setRecords([]);
        setSelected(new Set());
        setClearPolicy(false); // reset on new load

        try {
            const res = await Request({
                method: 'GET',
                route: `api/dunnings/${policyNo.trim()}/email/${email.trim()}`,
                token
            });
            const data = await res.json();

            if (!res.ok) {
                const errMsg = data.message || data.error || t('somethingWentWrong');
                showToast(errMsg, { error: true });
                return;
            }

            const list = Array.isArray(data) ? data : data.items || [];
            setRecords(list);
            if (!list.length) {
                showToast(t('No dunnings found for the given policy/email'), { error: false });
            }
        } catch (err) {
            showToast(err.message || t('somethingWentWrong'), { error: true });
        } finally {
            setIsLoading(false);
        }
    };

    const removeSelected = async () => {
        if (!hasSelection) return;
        setIsRemoving(true);
        try {
            const payload = {
                ids: Array.from(selected),
                clearPolicy,
                policyNo: policyNo.trim()
            };

            const res = await Request({
                method: 'POST',
                route: `api/dunnings/remove-failed-payments`,
                token,
                values: payload
            });
            const data = await res.json();

            if (!res.ok) {
                const errMsg = data.message || data.error || t('somethingWentWrong');
                showToast(errMsg, { error: true });
                return;
            }

            showToast(t('Selected dunnings removed successfully'), { success: true });
            setIsCompleted(true);
        } catch (err) {
            showToast(err.message || t('somethingWentWrong'), { error: true });
        } finally {
            setIsRemoving(false);
        }
    };

    if (isCompleted) {
        return (
            <>
                <div
                    className="modal_container__body__text"
                    style={{ maxWidth: '500px', margin: '40px 0', textAlign: 'start' }}
                >
                    {t('Selected dunnings removed successfully!')}
                </div>
                <div className="modal_container__body__row-modal d-flex gap-3">
                    <div
                        className={`button_red_small ${isMobileOnly ? 'mobile' : ''}`}
                        onClick={() => onCloseModal?.()}
                    >
                        {t('OK')}
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="wrapper w-100 d-flex gap-4 flex-column h-auto">
            <div className="modal_container__body__inputs align-items-start">
                <label htmlFor="sel-policy-no">{t('Policy Number')}</label>
                <input
                    id="sel-policy-no"
                    className="modal_container__body__input w-100"
                    placeholder="123456789"
                    value={policyNo}
                    onChange={(e) => setPolicyNo(e.target.value)}
                />
            </div>

            <div className="modal_container__body__inputs align-items-start">
                <label htmlFor="sel-email">{t('Email')}</label>
                <input
                    id="sel-email"
                    type="email"
                    className="modal_container__body__input w-100"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </div>

            <div className="modal_container__body__row-modal d-flex gap-3">
                {isLoading ? (
                    <Loading noText fast width="100%" scroll={false} />
                ) : (
                    <>
                        <button
                            className="button_red_small__admin w-100"
                            disabled={!policyNo || !email}
                            onClick={loadRecords}
                        >
                            {t('Load failed payments')}
                        </button>
                        <button className="button_red_small w-100" onClick={onBack}>
                            {t('Back')}
                        </button>
                    </>
                )}
            </div>

            {!!records.length && (
                <>
                    {/* Clear Policy Toggle — always visible */}
                    <div className="d-flex align-items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            id="clear-policy"
                            checked={clearPolicy}
                            onChange={(e) => setClearPolicy(e.target.checked)}
                        />
                        <label htmlFor="clear-policy">{t('Clear policy information')}</label>
                    </div>

                    <div className="d-flex flex-column gap-3 mt-2" style={{ maxHeight: 300, overflow: 'auto' }}>
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="checkbox"
                                id="check-all"
                                onChange={(e) => toggleAll(e.target.checked)}
                                checked={allSelected}
                            />
                            <label htmlFor="check-all">{t('Select all')}</label>
                        </div>

                        {records.map((r) => (
                            <div key={r.id} className="d-flex align-items-center gap-3 border p-2 rounded">
                                <input
                                    type="checkbox"
                                    id={`rec-${r.id}`}
                                    checked={selected.has(r.id)}
                                    onChange={(e) => toggle(r.id, e.target.checked)}
                                />
                                <label htmlFor={`rec-${r.id}`} className="w-100">
                                    <div className="d-flex justify-content-between flex-wrap gap-2">
                                        <span>
                                            <b>{t('Created at')}:</b> {formatYMD(r.created_at)}
                                        </span>
                                        <span>
                                            <b>{t('Due date')}:</b> {formatYMD(r.due_date)}
                                        </span>
                                        <span>
                                            <b>{t('Amount')}:</b> {formatAmount(r.due_amount)}
                                        </span>
                                    </div>
                                    {r.note && <div className="text-muted small mt-1">{r.note}</div>}
                                </label>
                            </div>
                        ))}

                        <div className="modal_container__body__row-modal mt-2">
                            {isRemoving ? (
                                <Loading noText fast width="100%" scroll={false} />
                            ) : (
                                <button
                                    className={`button_red_small__admin w-100 ${isMobileOnly ? 'mobile' : ''}`}
                                    disabled={!hasSelection}
                                    onClick={removeSelected}
                                >
                                    {t('Remove selected')}
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default RemoveDunningsSelective;
